/**
 * 字符预算流式渲染器
 *
 * 架构：API chunk → push() → 内部 buffer（全速写入）→ RAF 逐帧消费 → onRender → UI
 *
 * 特点：
 * - 渲染速度由 charsPerSecond 精确控制，不受 API 速率影响
 * - RAF 循环零 timer 开销，切后台自动暂停
 * - skipMode：页面隐藏时跳过逐字动画，新数据即时渲染
 * - destroy() 可中断并立即显示全文，用于主动终止流水线
 *
 * @param onRender       - 每帧回调，接收当前应显示的完整文本
 * @param onComplete     - 缓冲区全部消费完且 end() 已调用后触发（仅一次）
 * @param charsPerSecond - 每秒显示字符数，默认 80
 * @returns { push, end, destroy, setSkipMode }
 */
export function createStreamRenderer(onRender: (text: string) => void, onComplete: () => void, initialCps = 80) {
  let buffer = ''
  let visibleEnd = 0
  let budget = 0
  let rafId = 0
  let streamEnded = false
  let completed = false
  let destroyed = false
  let skipMode = false
  let charsPerSecond = initialCps
  /** 上一帧的时间戳（performance.now），用于帧率无关的字符预算计算 */
  let lastFrameTime = 0

  const flushNow = () => {
    if (rafId !== 0) {
      cancelAnimationFrame(rafId)
      rafId = 0
    }
    onRender(buffer)
    visibleEnd = buffer.length
  }

  const tryComplete = () => {
    if (!completed && streamEnded && visibleEnd >= buffer.length) {
      completed = true
      onComplete()
    }
  }

  const reveal = () => {
    if (destroyed || skipMode) return
    rafId = requestAnimationFrame(() => {
      rafId = 0
      if (destroyed || skipMode) return

      // 帧率无关预算：用实际经过时间（秒）× charsPerSecond 替代硬编码 /60
      // lastFrameTime 在 push/setCharsPerSecond 启动新 cycle 时已初始化为当前时间
      const now = performance.now()
      const elapsed = (now - lastFrameTime) / 1000
      budget += charsPerSecond * elapsed
      lastFrameTime = now

      const advance = Math.floor(budget)
      if (advance > 0) {
        budget -= advance
        visibleEnd = Math.min(visibleEnd + advance, buffer.length)
        onRender(buffer.slice(0, visibleEnd))
      }

      if (visibleEnd < buffer.length) {
        reveal()
      } else {
        tryComplete()
      }
    })
  }

  return {
    /** 追加数据块。快速通道（charsPerSecond >= 99999 或 skipMode）下即时渲染全文 */
    push(chunk: string) {
      if (destroyed) return
      buffer += chunk
      if (charsPerSecond >= 99999 || skipMode) {
        flushNow()
        tryComplete()
      } else if (rafId === 0) {
        lastFrameTime = performance.now()
        reveal()
      }
    },

    /** 标记数据流结束。缓冲区已空则直接完成，否则等 RAF 追上 */
    end() {
      if (destroyed) return
      streamEnded = true
      if (visibleEnd >= buffer.length) {
        tryComplete()
      }
    },

    /** 立即终止渲染：取消 RAF，一次性显示全文，触发 onComplete。幂等。 */
    destroy() {
      if (destroyed) return
      destroyed = true
      streamEnded = true
      if (rafId !== 0) {
        cancelAnimationFrame(rafId)
        rafId = 0
      }
      onRender(buffer)
      if (!completed) {
        completed = true
        onComplete()
      }
    },

    /**
     * 设置快进模式。
     * - 进入时：立即渲染全部缓冲，取消 RAF
     * - 退出时：不做任何事（已显示全文，无需回退逐字动画）
     */
    setSkipMode(skip: boolean) {
      if (destroyed) return
      skipMode = skip
      if (skip && buffer.length > 0) {
        flushNow()
        tryComplete()
      }
    },

    /**
     * 动态调整渲染速度。
     * - 若有未消费缓冲且无活跃 RAF，立即启动一帧让新速度生效
     */
    setCharsPerSecond(cps: number) {
      if (destroyed) return
      charsPerSecond = cps
      // 缓冲区还有内容且 RAF 未在运行：立即消费一帧
      if (visibleEnd < buffer.length && rafId === 0 && !skipMode && cps < 99999) {
        lastFrameTime = performance.now()
        reveal()
      }
    },
  }
}
